import type { Properties } from 'posthog-js';
import { fijarPropsDePersonaUnaVez, trackEvent } from './posthog';
import { checkIsStandalone } from '../hooks/useDeviceDetection';

// Medición de "¿cuánta gente usa Atlas instalada como app?".
// Ver ADR-2026-08-04-medicion-instalacion-pwa.
//
// LO QUE HAY QUE ENTENDER ANTES DE TOCAR ESTO:
//
// 1. **No se puede contar "instalaciones" parejo entre plataformas.** iOS no
//    tiene `beforeinstallprompt` ni `appinstalled`: agregar a pantalla de inicio
//    es una acción del sistema operativo, invisible para la web. Y en
//    Android/desktop el evento `appinstalled` sí existe, pero solo lo vemos si
//    la pestaña está abierta cuando pasa. Por eso la métrica principal NO es
//    "instalaciones" sino **"usuarios que abrieron la app en standalone"**, que
//    además es la que importa: instalar y no volver no vale nada.
// 2. **El user-agent NO sirve para esto.** En Android el UA de la PWA instalada
//    es idéntico al de Chrome, y en iOS el del standalone se confunde con los
//    webviews in-app (Instagram y compañía). De ahí que `profiles.datos_dispositivo`
//    no pueda responder la pregunta.
// 3. **El modo es por apertura, no por usuario.** El mismo tipo entra un día
//    desde el ícono y otro desde un link del navegador. Por eso además de la
//    super property (que etiqueta cada evento) marcamos al usuario con
//    `usa_pwa_instalada` la primera vez que lo vemos en standalone, y esa marca
//    ya no se pisa.

const KEY_SESION = 'atlas-pwa-apertura-trackeada';

export type ModoVisualizacion =
  | 'standalone'
  | 'fullscreen'
  | 'minimal-ui'
  | 'window-controls-overlay'
  | 'browser';

function detectarModo(): ModoVisualizacion {
  // `checkIsStandalone` cubre la media query estándar Y el `navigator.standalone`
  // de iOS, que es el único lugar donde iOS lo dice.
  if (checkIsStandalone()) return 'standalone';
  const otros: ModoVisualizacion[] = [
    'fullscreen',
    'minimal-ui',
    'window-controls-overlay',
  ];
  for (const modo of otros) {
    try {
      if (window.matchMedia(`(display-mode: ${modo})`).matches) return modo;
    } catch {
      /* matchMedia con un modo desconocido: seguimos */
    }
  }
  return 'browser';
}

/**
 * Super properties del arranque. Se pasan a `initPostHog` (y no se registran
 * después) para que el `$pageview` inicial ya salga etiquetado.
 */
export function superPropsPwa(): Properties {
  const modo = detectarModo();
  return { pwa_standalone: modo !== 'browser', pwa_modo_visualizacion: modo };
}

/**
 * Se llama una vez, en el arranque, DESPUÉS de `initPostHog(superPropsPwa())`.
 * Marca al usuario, cuenta la apertura y engancha `appinstalled`.
 */
export function initPwaAnalytics(): void {
  const modo = detectarModo();

  if (modo !== 'browser') {
    // Marca pegajosa en la persona: "este usuario alguna vez usó la app
    // instalada". Viaja con el próximo identify (ver `fijarPropsDePersonaUnaVez`).
    fijarPropsDePersonaUnaVez({
      usa_pwa_instalada: true,
      pwa_primer_uso: new Date().toISOString(),
    });

    // Una apertura = una sesión de pestaña. Sin este guard, cada recarga o cada
    // navegación que remonte la app contaría de nuevo.
    let yaTrackeada = false;
    try {
      yaTrackeada = sessionStorage.getItem(KEY_SESION) === '1';
      sessionStorage.setItem(KEY_SESION, '1');
    } catch {
      /* Safari en modo privado tira acá: preferimos contar de más que no contar */
    }
    if (!yaTrackeada) trackEvent('pwa_abierta', { pwa_modo_visualizacion: modo });
  }

  // Android/desktop: el navegador avisa cuando la instalación termina. iOS no
  // dispara esto nunca — ahí la única señal es la apertura en standalone.
  try {
    window.addEventListener('appinstalled', () => {
      trackEvent('pwa_instalada', { origen: 'evento_appinstalled' });
    });
  } catch {
    /* noop */
  }
}

// ─── Embudo del cartel de instalación (`AddToHomescreenPrompt`) ──────────────
// `origen` = desde qué pantalla se mostró, para saber qué ubicación convierte.

export function trackPromptVisto(props: Properties): void {
  trackEvent('pwa_prompt_visto', props);
}

export function trackPromptDescartado(props: Properties): void {
  trackEvent('pwa_prompt_descartado', props);
}

export function trackPromptInstalarClick(props: Properties): void {
  trackEvent('pwa_prompt_instalar_click', props);
}

/**
 * El usuario aceptó el diálogo nativo (solo Chrome/Edge). El caso "lo canceló"
 * no va acá: sale como `pwa_prompt_descartado` con su motivo, para no tener dos
 * eventos distintos contando lo mismo.
 */
export function trackInstaladaDesdePromptNativo(props: Properties): void {
  trackEvent('pwa_instalada', { ...props, origen: 'prompt_nativo' });
}
